import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import { Card } from "@mui/material";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiProgress from "components/VuiProgress";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import LineChart from "examples/Charts/LineCharts/LineChart";
import BarChart from "examples/Charts/BarCharts/BarChart";

import { IoWallet, IoCalendar, IoTrendingUp } from "react-icons/io5";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";
import { getSummary, getExpenses } from "services/api";

const CAT_COLORS = [
  "#0075FF", "#2CD9FF", "#01B574", "#FFB547", "#E31A1A",
  "#7928CA", "#FF0080", "#00D4FF", "#FF6B6B", "#4ECDC4",
];

function Dashboard() {
  const [data, setData] = useState(null);
  const [recentTxns, setRecentTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getSummary(), getExpenses()]).then(([summary, txns]) => {
      setData(summary);
      setRecentTxns((txns || []).slice(0, 8));
      setLoading(false);
    });
  }, []);

  if (loading || !data) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <VuiTypography color="white" variant="h5">Loading...</VuiTypography>
        </VuiBox>
      </DashboardLayout>
    );
  }

  const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

  // Budget values from API (single source of truth)
  const budgetDaily = data.budget_daily || 25000;
  const budgetWeekly = data.budget_weekly || 100000;
  const budgetMonthly = data.budget_monthly || 300000;

  const todayRemaining = data.today_remaining ?? (budgetDaily - data.today_spend);
  const weekRemaining = data.week_remaining ?? (budgetWeekly - data.week_spend);
  const monthRemaining = data.month_remaining ?? (budgetMonthly - data.month_spend);

  const todayPct = Math.min((data.today_spend / budgetDaily) * 100, 100);
  const weekPct = Math.min((data.week_spend / budgetWeekly) * 100, 100);
  const monthPct = Math.min((data.month_spend / budgetMonthly) * 100, 100);

  const todayOver = data.today_spend > budgetDaily;
  const weekOver = data.week_spend > budgetWeekly;
  const monthOver = data.month_spend > budgetMonthly;

  const projectedSpend = data.projected_month_spend || 0;
  const daysRemaining = data.days_remaining_in_month || 0;
  const spendChange = data.spend_change_pct || 0;
  const prevSpend = data.prev_month_spend || 0;

  // Income display
  const incomeUSD = data.month_income_usd || 0;
  const incomeGBP = data.month_income_gbp || 0;
  const incomePKR = data.month_income || 0;
  let incomeStr = "";
  if (incomePKR > 0) incomeStr += `Rs. ${fmt(incomePKR)}`;
  if (incomeUSD > 0) incomeStr += `${incomeStr ? " + " : ""}$${fmt(incomeUSD)}`;
  if (incomeGBP > 0) incomeStr += `${incomeStr ? " + " : ""}£${fmt(incomeGBP)}`;
  if (!incomeStr) incomeStr = "Rs. 0";

  // Weekly category data
  const weekCatTotals = data.week_category_totals || {};
  const weekCatCount = data.week_category_count || {};
  const weekCatNames = Object.keys(weekCatTotals).sort((a, b) => weekCatTotals[b] - weekCatTotals[a]);
  const weekCatValues = weekCatNames.map((k) => weekCatTotals[k]);
  const weekTotal = weekCatValues.reduce((a, b) => a + b, 0) || 1;

  // Horizontal bar chart for weekly categories
  const barChartData = [{
    name: "Spent",
    data: weekCatValues,
  }];
  const barChartOptions = {
    chart: { toolbar: { show: false } },
    plotOptions: {
      bar: { horizontal: true, barHeight: "60%", borderRadius: 4 },
    },
    dataLabels: {
      enabled: true,
      formatter: (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v),
      style: { colors: ["#fff"], fontSize: "11px" },
    },
    xaxis: {
      categories: weekCatNames,
      labels: {
        style: { colors: "#c8cfca", fontSize: "10px" },
        formatter: (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v),
      },
    },
    yaxis: {
      labels: { style: { colors: "#c8cfca", fontSize: "11px" } },
    },
    tooltip: { theme: "dark", y: { formatter: (v) => `Rs. ${Number(v).toLocaleString()}` } },
    grid: { borderColor: "#56577A", strokeDashArray: 5 },
    colors: CAT_COLORS.slice(0, weekCatNames.length),
    fill: { opacity: 1 },
    legend: { show: false },
  };

  // Daily spending chart
  const dailyTrend = data.daily_trend || [];
  const chartLabels = dailyTrend.map((d) => {
    const p = (d.date || d[0] || "").split("-");
    return p.length >= 3 ? `${p[2]}/${p[1]}` : "";
  });
  const chartValues = dailyTrend.map((d) => d.amount || d[1] || 0);

  const lineChartData = [{ name: "Spending", data: chartValues }];
  const lineChartOptions = {
    chart: { toolbar: { show: false }, dropShadow: { enabled: true, top: 5, left: 0, blur: 8, color: "#0075FF", opacity: 0.3 } },
    tooltip: { theme: "dark", y: { formatter: (v) => `Rs. ${Number(v).toLocaleString()}` } },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3 },
    xaxis: {
      type: "category", categories: chartLabels,
      labels: { style: { colors: "#c8cfca", fontSize: "10px" } },
      axisBorder: { show: false }, axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: "#c8cfca", fontSize: "10px" },
        formatter: (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v),
      },
    },
    legend: { show: false },
    grid: { strokeDashArray: 5, borderColor: "#56577A" },
    fill: {
      type: "gradient",
      gradient: { shade: "dark", type: "vertical", shadeIntensity: 0, opacityFrom: 0.4, opacityTo: 0, stops: [] },
    },
    colors: ["#0075FF"],
    markers: { size: 0 },
    annotations: {
      yaxis: [{
        y: budgetDaily,
        borderColor: "#E31A1A",
        strokeDashArray: 6,
        label: {
          text: `Budget: Rs. ${fmt(budgetDaily)}`,
          position: "left",
          style: { color: "#fff", background: "#E31A1A", fontSize: "10px", padding: { left: 4, right: 4, top: 2, bottom: 2 } },
        },
      }],
    },
  };

  // Top vendors
  const topVendors = (data.top_vendors || []).slice(0, 5);

  // Budget remaining card helper
  const BudgetCard = ({ label, icon, remaining, spent, budget, pct, over, subtitle }) => (
    <Card sx={{ p: 3, height: "100%", border: `1px solid ${over ? "rgba(231,76,60,0.4)" : "rgba(1,181,116,0.2)"}` }}>
      <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start">
        <VuiTypography variant="caption" color="text" fontWeight="bold" textTransform="uppercase">
          {label}
        </VuiTypography>
        <VuiBox
          display="flex" justifyContent="center" alignItems="center"
          sx={{ width: "36px", height: "36px", borderRadius: "10px", background: over ? "rgba(231,76,60,0.15)" : "rgba(1,181,116,0.15)" }}
        >
          {icon}
        </VuiBox>
      </VuiBox>
      <VuiTypography variant="h3" color={over ? "error" : "success"} fontWeight="bold" mt="4px">
        {over ? "-" : ""}Rs. {fmt(Math.abs(remaining))} {over ? "over" : "left"}
      </VuiTypography>
      <VuiBox mt={1.5} mb={0.5}>
        <VuiProgress value={pct} color={over ? "error" : "info"} sx={{ background: "#2D2E5F" }} />
      </VuiBox>
      <VuiBox display="flex" justifyContent="space-between">
        <VuiTypography variant="caption" color="text">
          Spent Rs. {fmt(spent)} of Rs. {fmt(budget)}
        </VuiTypography>
        <VuiTypography variant="caption" color={over ? "error" : "text"} fontWeight="bold">
          {pct.toFixed(0)}% used
        </VuiTypography>
      </VuiBox>
      {subtitle && (
        <VuiTypography variant="caption" color="text" mt="4px" display="block">
          {subtitle}
        </VuiTypography>
      )}
    </Card>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>

        {/* ROW 1 — Budget Remaining Hero */}
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <BudgetCard
                label="Today"
                icon={<IoWallet size="18px" color={todayOver ? "#E31A1A" : "#01B574"} />}
                remaining={todayRemaining}
                spent={data.today_spend}
                budget={budgetDaily}
                pct={todayPct}
                over={todayOver}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <BudgetCard
                label="This Week"
                icon={<IoCalendar size="18px" color={weekOver ? "#E31A1A" : "#01B574"} />}
                remaining={weekRemaining}
                spent={data.week_spend}
                budget={budgetWeekly}
                pct={weekPct}
                over={weekOver}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <BudgetCard
                label="This Month"
                icon={<IoTrendingUp size="18px" color={monthOver ? "#E31A1A" : "#01B574"} />}
                remaining={monthRemaining}
                spent={data.month_spend}
                budget={budgetMonthly}
                pct={monthPct}
                over={monthOver}
                subtitle={`Projection: Rs. ${fmt(projectedSpend)} by month end`}
              />
            </Grid>
          </Grid>
        </VuiBox>

        {/* ROW 2 — This Week's Spending by Category */}
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={7}>
              <Card sx={{ p: 3, height: "100%" }}>
                <VuiTypography variant="lg" color="white" fontWeight="bold" mb="5px">
                  This Week by Category
                </VuiTypography>
                <VuiTypography variant="button" color="text" fontWeight="regular" mb="16px">
                  PKR spending breakdown
                </VuiTypography>
                <VuiBox sx={{ height: "300px" }}>
                  {weekCatNames.length > 0 ? (
                    <BarChart barChartData={barChartData} barChartOptions={barChartOptions} />
                  ) : (
                    <VuiBox display="flex" justifyContent="center" alignItems="center" height="100%">
                      <VuiTypography color="text" variant="button">No spending this week</VuiTypography>
                    </VuiBox>
                  )}
                </VuiBox>
              </Card>
            </Grid>
            <Grid item xs={12} lg={5}>
              <Card sx={{ p: 3, height: "100%" }}>
                <VuiTypography variant="lg" color="white" fontWeight="bold" mb="16px">
                  Category Details
                </VuiTypography>
                {weekCatNames.length > 0 ? weekCatNames.map((cat, i) => {
                  const catPct = ((weekCatTotals[cat] / weekTotal) * 100).toFixed(1);
                  const txnCount = weekCatCount[cat] || 0;
                  return (
                    <VuiBox key={cat} display="flex" justifyContent="space-between" alignItems="center" py={1}
                      sx={{ borderBottom: i < weekCatNames.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none" }}
                    >
                      <VuiBox display="flex" alignItems="center">
                        <VuiBox sx={{ width: "10px", height: "10px", borderRadius: "3px", background: CAT_COLORS[i % CAT_COLORS.length], mr: 1.5, flexShrink: 0 }} />
                        <VuiBox>
                          <VuiTypography variant="button" color="white" fontWeight="medium">{cat}</VuiTypography>
                          <VuiTypography variant="caption" color="text" display="block">
                            {txnCount} txn{txnCount !== 1 ? "s" : ""} &middot; {catPct}%
                          </VuiTypography>
                        </VuiBox>
                      </VuiBox>
                      <VuiTypography variant="button" color="white" fontWeight="bold">
                        Rs. {fmt(weekCatTotals[cat])}
                      </VuiTypography>
                    </VuiBox>
                  );
                }) : (
                  <VuiBox display="flex" justifyContent="center" alignItems="center" height="200px">
                    <VuiTypography color="text" variant="button">No spending this week</VuiTypography>
                  </VuiBox>
                )}
              </Card>
            </Grid>
          </Grid>
        </VuiBox>

        {/* ROW 3 — Monthly Overview */}
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: "100%" }}>
                <VuiTypography variant="caption" color="text" fontWeight="bold" textTransform="uppercase">
                  Month Projection
                </VuiTypography>
                <VuiTypography variant="h3" color={projectedSpend > budgetMonthly ? "error" : "white"} fontWeight="bold" mt="4px">
                  Rs. {fmt(projectedSpend)}
                </VuiTypography>
                <VuiTypography variant="caption" color="text">
                  projected by month end
                </VuiTypography>
                <VuiBox mt={1} p={1.5} sx={{ background: "rgba(0,117,255,0.08)", borderRadius: "8px" }}>
                  <VuiTypography variant="caption" color="text">
                    {daysRemaining} days remaining &middot; Avg Rs. {fmt(data.daily_avg)}/day
                  </VuiTypography>
                </VuiBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: "100%" }}>
                <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start">
                  <VuiBox>
                    <VuiTypography variant="caption" color="text" fontWeight="bold" textTransform="uppercase">
                      Money In
                    </VuiTypography>
                    <VuiTypography variant="h3" color="success" fontWeight="bold" mt="4px">
                      {incomeStr}
                    </VuiTypography>
                    <VuiTypography variant="caption" color="text">
                      {(data.income_items || []).length} received this month
                    </VuiTypography>
                  </VuiBox>
                  <VuiBox
                    display="flex" justifyContent="center" alignItems="center"
                    sx={{ width: "42px", height: "42px", borderRadius: "12px", background: "rgba(1,181,116,0.15)" }}
                  >
                    <FaArrowDown color="#01B574" size="18px" />
                  </VuiBox>
                </VuiBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, height: "100%" }}>
                <VuiTypography variant="caption" color="text" fontWeight="bold" textTransform="uppercase">
                  vs Last Month
                </VuiTypography>
                <VuiTypography variant="h3" color={spendChange <= 0 ? "success" : "error"} fontWeight="bold" mt="4px">
                  {spendChange > 0 ? "+" : ""}{spendChange}%
                </VuiTypography>
                <VuiTypography variant="caption" color="text">
                  {spendChange <= 0 ? "less" : "more"} spending
                </VuiTypography>
                <VuiBox mt={1} p={1.5} sx={{ background: "rgba(0,117,255,0.08)", borderRadius: "8px" }}>
                  <VuiTypography variant="caption" color="text">
                    Last month: Rs. {fmt(prevSpend)}
                  </VuiTypography>
                </VuiBox>
              </Card>
            </Grid>
          </Grid>
        </VuiBox>

        {/* ROW 4 — Daily Spending Chart (full width) */}
        <VuiBox mb={3}>
          <Card>
            <VuiBox sx={{ height: "100%" }} p={3}>
              <VuiTypography variant="lg" color="white" fontWeight="bold" mb="5px">
                Daily Spending
              </VuiTypography>
              <VuiTypography variant="button" color="text" fontWeight="regular" mb="20px" display="block">
                Last 30 days (PKR) &middot; Red line = Rs. {fmt(budgetDaily)} daily budget
              </VuiTypography>
              <VuiBox sx={{ height: "310px" }}>
                {chartValues.length > 0 ? (
                  <LineChart lineChartData={lineChartData} lineChartOptions={lineChartOptions} />
                ) : (
                  <VuiTypography color="text" variant="button">No data</VuiTypography>
                )}
              </VuiBox>
            </VuiBox>
          </Card>
        </VuiBox>

        {/* ROW 5 — Top Vendors + Recent Transactions */}
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={5}>
              <Card sx={{ p: 3, height: "100%" }}>
                <VuiTypography variant="lg" color="white" fontWeight="bold" mb="4px">
                  Top Vendors
                </VuiTypography>
                <VuiTypography variant="button" color="text" fontWeight="regular" mb="16px">
                  By amount spent
                </VuiTypography>
                {topVendors.map((v, i) => (
                  <VuiBox key={i} display="flex" justifyContent="space-between" alignItems="center" py={0.8}>
                    <VuiBox display="flex" alignItems="center">
                      <VuiBox
                        bgColor="info"
                        display="flex" justifyContent="center" alignItems="center"
                        sx={{ borderRadius: "8px", width: "28px", height: "28px", mr: 1.5 }}
                      >
                        <VuiTypography variant="caption" color="white" fontWeight="bold">
                          {(v.name || "?")[0].toUpperCase()}
                        </VuiTypography>
                      </VuiBox>
                      <VuiBox>
                        <VuiTypography variant="button" color="white" fontWeight="medium">
                          {(v.name || "Unknown").slice(0, 20)}
                        </VuiTypography>
                        <VuiTypography variant="caption" color="text" display="block">
                          {v.count} txns
                        </VuiTypography>
                      </VuiBox>
                    </VuiBox>
                    <VuiTypography variant="button" color="white" fontWeight="bold">
                      {v.currency} {fmt(v.total)}
                    </VuiTypography>
                  </VuiBox>
                ))}
              </Card>
            </Grid>
            <Grid item xs={12} lg={7}>
              <Card sx={{ p: 3, height: "100%" }}>
                <VuiTypography variant="lg" color="white" fontWeight="bold" mb="16px">
                  Recent Transactions
                </VuiTypography>
                {recentTxns.length > 0 ? recentTxns.map((txn, i) => {
                  const isCredit = (txn.direction || "debit") === "credit";
                  return (
                    <VuiBox
                      key={i}
                      display="flex" justifyContent="space-between" alignItems="center" py={1}
                      sx={{ borderBottom: i < recentTxns.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none" }}
                    >
                      <VuiBox display="flex" alignItems="center">
                        <VuiBox
                          display="flex" justifyContent="center" alignItems="center"
                          sx={{
                            width: "32px", height: "32px", borderRadius: "8px", mr: 1.5, flexShrink: 0,
                            background: isCredit ? "rgba(1,181,116,0.15)" : "rgba(231,76,60,0.15)",
                          }}
                        >
                          {isCredit
                            ? <FaArrowDown color="#01B574" size="12px" />
                            : <FaArrowUp color="#E31A1A" size="12px" />
                          }
                        </VuiBox>
                        <VuiBox>
                          <VuiTypography variant="button" color="white" fontWeight="medium">
                            {(txn.vendor || txn.description || "Unknown").slice(0, 30)}
                          </VuiTypography>
                          <VuiTypography variant="caption" color="text" display="block">
                            {(txn.timestamp || "").slice(0, 10)} &middot; {txn.category || "Other"}
                          </VuiTypography>
                        </VuiBox>
                      </VuiBox>
                      <VuiTypography variant="button" color={isCredit ? "success" : "white"} fontWeight="bold">
                        {isCredit ? "+" : "-"}{txn.currency || "PKR"} {fmt(txn.amount)}
                      </VuiTypography>
                    </VuiBox>
                  );
                }) : (
                  <VuiTypography color="text" variant="button">No recent transactions</VuiTypography>
                )}
              </Card>
            </Grid>
          </Grid>
        </VuiBox>

      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
